import React, { useState } from "react";
import { useParams } from "react-router-dom";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const EventDetails = () => {
  const { eventId } = useParams();

  // Tabs state
  const [activeTab, setActiveTab] = useState("overview");

  // Mock events data - in a real app this would come from an API
  const mockEvents = {
    1: {
      id: 1,
      name: "Retirement Planning Seminar",
      date: "2024-04-15T14:00:00",
      type: "In-Person",
      subType: "Seminar",
      status: "Scheduled",
      description:
        "A comprehensive seminar covering essential retirement planning strategies.",
      location: "123 Financial Center, Suite 400",
      metrics: {
        rsvps: 45,
        pendingTasks: 3,
        attendance: null,
      },
    },
    2: {
      id: 2,
      name: "Investment Strategy Webinar",
      date: "2024-04-20T10:00:00",
      type: "Virtual",
      subType: "External Webinar",
      status: "In Progress",
      description:
        "Learn about the latest investment strategies and market trends.",
      location: "https://webinar.example.com/investment-strategy",
      metrics: {
        rsvps: 120,
        pendingTasks: 5,
        attendance: null,
      },
    },
    3: {
      id: 3,
      name: "Client Appreciation Night",
      date: "2024-05-01T18:00:00",
      type: "In-Person",
      subType: "Networking",
      status: "Scheduled",
      description: "An evening to celebrate and thank our valued clients.",
      location: "Grand Hotel Ballroom",
      metrics: {
        rsvps: 75,
        pendingTasks: 8,
        attendance: null,
      },
    },
    4: {
      id: 4,
      name: "Tax Planning Workshop",
      date: "2024-02-15T15:00:00",
      type: "In-Person",
      subType: "Educational",
      status: "Completed",
      description:
        "A comprehensive workshop covering tax planning strategies for retirement.",
      location: "123 Financial Center, Suite 300",
      metrics: {
        rsvps: 60,
        attendance: 52,
        feedback: 4.8,
        followUps: 15,
        engagementRate: "87%",
        satisfactionScore: "92%",
      },
    },
  };

  // Get the event data based on the ID
  const event = mockEvents[eventId];

  // If event not found, show error state
  if (!event) {
    return (
      <SubPageLayout
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Events", path: "/advisor-portal/events" },
          { label: "Event Not Found", path: "#" },
        ]}
        title="Event Not Found"
      >
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Event not found</h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            The event you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </SubPageLayout>
    );
  }

  const mockTeamMembers = [
    { id: 1, name: "John Smith", role: "Host", avatar: "JS" },
    { id: 2, name: "Sarah Johnson", role: "Technical Support", avatar: "SJ" },
    { id: 3, name: "Mike Wilson", role: "Client Relations", avatar: "MW" },
  ];

  const mockTasks = [
    {
      id: 1,
      title: "Send reminder emails",
      status: "completed",
      dueDate: "2024-04-14",
    },
    {
      id: 2,
      title: "Prepare presentation slides",
      status: "in-progress",
      dueDate: "2024-04-14",
    },
    {
      id: 3,
      title: "Review attendee list",
      status: "pending",
      dueDate: "2024-04-14",
    },
  ];

  const mockMetrics = {
    rsvps: {
      total: 45,
      manual: 15,
      automated: 30,
      trend: [
        { date: "2024-03-01", count: 10 },
        { date: "2024-03-15", count: 25 },
        { date: "2024-04-01", count: 45 },
      ],
    },
    promotions: {
      emailCTR: "24%",
      socialEngagement: "156 interactions",
      pendingTasks: 3,
    },
  };

  const mockTimeline = [
    { date: "2024-04-01", event: "Event Created", status: "completed" },
    { date: "2024-04-10", event: "Invitations Sent", status: "completed" },
    { date: "2024-04-14", event: "Reminder Emails", status: "pending" },
    { date: "2024-04-15", event: "Event Day", status: "upcoming" },
  ];

  const mockAttendees = [
    {
      id: 1,
      name: "John Smith",
      status: "Attended",
      feedback: 5,
      followUpStatus: "Completed",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      status: "Attended",
      feedback: 4,
      followUpStatus: "Scheduled",
    },
    {
      id: 3,
      name: "Mike Wilson",
      status: "No Show",
      feedback: null,
      followUpStatus: "Pending",
    },
  ];

  const mockFeedback = [
    {
      id: 1,
      rating: 5,
      comment:
        "Very informative session. The strategies discussed were practical and actionable.",
    },
    {
      id: 2,
      rating: 4,
      comment: "Good presentation. Would have liked more time for Q&A.",
    },
    {
      id: 3,
      rating: 5,
      comment:
        "Excellent workshop! The case studies were particularly helpful.",
    },
  ];

  const mockFollowUps = [
    {
      id: 1,
      type: "Call",
      date: "2024-02-20",
      status: "Completed",
      outcome: "Scheduled consultation",
    },
    {
      id: 2,
      type: "Email",
      date: "2024-02-22",
      status: "Completed",
      outcome: "Requested more information",
    },
    {
      id: 3,
      type: "Meeting",
      date: "2024-03-01",
      status: "Scheduled",
      outcome: "Pending",
    },
  ];

  const isPastEvent = new Date(event.date) < new Date();

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Events", path: "/advisor-portal/events" },
        { label: event?.name || "Event Details", path: "#" },
      ]}
      title={event?.name || "Event Details"}
    >
      <div className="space-y-6">
        {/* Top Section: Event Summary */}
        <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="p-6 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {event?.name}
                  </h1>
                  <span
                    className={`px-3 py-1 text-sm rounded-full font-medium ${
                      event?.status === "Completed"
                        ? "bg-green-100 text-green-800"
                        : event?.status === "In Progress"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {event?.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>
                      {new Date(event?.date).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {new Date(event?.date).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>{event?.location}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 rounded-xl hover:bg-gray-200 
                  transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Add to Calendar</span>
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 
                  transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span>
                    {event?.type === "Virtual" ? "Join Event" : "View Location"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 divide-x divide-gray-100">
            <div className="p-6 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {event?.metrics?.rsvps}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">Total RSVPs</div>
            </div>
            {isPastEvent ? (
              <>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {event.metrics.attendance}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Attended</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {event.metrics.feedback}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Avg. Rating</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {event.metrics.followUps}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Follow-ups</div>
                </div>
              </>
            ) : (
              <>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {event?.metrics?.pendingTasks}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Pending Tasks</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">--</div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Attendance</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">--</div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Rating</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: "overview", label: "Overview" },
              ...(isPastEvent
                ? [
                    { id: "attendees", label: "Attendees" },
                    { id: "feedback", label: "Feedback" },
                    { id: "followups", label: "Follow-ups" },
                  ]
                : [
                    { id: "tasks", label: "Tasks" },
                    { id: "team", label: "Team" },
                  ]),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                } transition-colors`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="col-span-2 space-y-6">
            {activeTab === "overview" && (
              <>
                <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Event Description
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">{event.description}</p>

                  {isPastEvent && (
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <div className="bg-green-50 rounded-xl p-4">
                        <div className="text-sm font-medium text-green-800">
                          Engagement Rate
                        </div>
                        <div className="mt-1 text-2xl font-bold text-green-900">
                          {event.metrics.engagementRate}
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <div className="text-sm font-medium text-blue-800">
                          Satisfaction Score
                        </div>
                        <div className="mt-1 text-2xl font-bold text-blue-900">
                          {event.metrics.satisfactionScore}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Team Members */}
                <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Team Members
                  </h2>
                  <div className="space-y-4">
                    {mockTeamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 
                            flex items-center justify-center font-medium"
                          >
                            {member.avatar}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {member.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-500">
                              {member.role}
                            </div>
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Event Timeline
                  </h2>
                  <div className="space-y-4">
                    {mockTimeline.map((item, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div
                          className={`w-2 h-2 mt-2 rounded-full ${
                            item.status === "completed"
                              ? "bg-green-500"
                              : item.status === "pending"
                                ? "bg-yellow-500"
                                : "bg-gray-300"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {item.event}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-500">
                            {item.date}
                          </div>
                        </div>
                        {item.status === "completed" && (
                          <svg
                            className="w-5 h-5 text-green-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === "attendees" && isPastEvent && (
              <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Attendees
                </h2>
                <div className="space-y-4">
                  {mockAttendees.map((attendee) => (
                    <div
                      key={attendee.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {attendee.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-500">
                          Status:{" "}
                          <span
                            className={`font-medium ${
                              attendee.status === "Attended"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {attendee.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {attendee.feedback && (
                          <div className="flex items-center gap-1">
                            <svg
                              className="w-5 h-5 text-yellow-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-sm font-medium">
                              {attendee.feedback}
                            </span>
                          </div>
                        )}
                        <span
                          className={`px-3 py-1 text-sm rounded-full font-medium ${
                            attendee.followUpStatus === "Completed"
                              ? "bg-green-100 text-green-800"
                              : attendee.followUpStatus === "Scheduled"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800 dark:text-gray-200"
                          }`}
                        >
                          {attendee.followUpStatus}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "feedback" && isPastEvent && (
              <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Feedback
                </h2>
                <div className="space-y-4">
                  {mockFeedback.map((item) => (
                    <div key={item.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, index) => (
                          <svg
                            key={index}
                            className={`w-5 h-5 ${
                              index < item.rating
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">{item.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "followups" && isPastEvent && (
              <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Follow-ups
                </h2>
                <div className="space-y-4">
                  {mockFollowUps.map((followUp) => (
                    <div
                      key={followUp.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {followUp.type}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-500">
                          {new Date(followUp.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-3 py-1 text-sm rounded-full font-medium ${
                            followUp.status === "Completed"
                              ? "bg-green-100 text-green-800"
                              : followUp.status === "Scheduled"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800 dark:text-gray-200"
                          }`}
                        >
                          {followUp.status}
                        </span>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {followUp.outcome}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Quick Actions
              </h2>
              <div className="space-y-3">
                {isPastEvent ? (
                  <>
                    <button
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span>Generate Report</span>
                    </button>
                    <button
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Schedule Follow-up Event</span>
                    </button>
                    <button
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Send Thank You Emails</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Send Reminder</span>
                    </button>
                    <button
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      <span>Edit Event</span>
                    </button>
                    <button
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                      transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span>Cancel Event</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default EventDetails;
